export type RemoveData<ForObjectType extends object> = {
	removeMethodName: keyof ForObjectType,
	shouldCleanup?: (obj: ForObjectType) => boolean
};
export type AddRemoveData<ForObjectType extends object> = {[key: string]: RemoveData<ForObjectType>}

export interface SupportsEventListenerManager<ForObjectType extends object = object> {
	getEventListenerManagerData(): AddRemoveData<ForObjectType>;
}
function isSupportsEventListenerManager(obj: any): obj is SupportsEventListenerManager {
	return typeof obj.getEventListenerManagerData === "function";
}

interface HasAddRemoveEventListeners {
	addEventListener(...args: any[]): any;
	removeEventListener(...args: any[]): any;
}
interface HasOnOffEventListeners {
	on(...args: any[]): any;
	off(...args: any[]): any;
}

type SupportedTypes = HasAddRemoveEventListeners | HasOnOffEventListeners;

const supportedTypesAddRemoveData: AddRemoveData<any> = {
	"addEventListener": {
		removeMethodName: "removeEventListener",
		shouldCleanup: (obj: any) => obj instanceof Node && !obj.isConnected
	},
	"on": { // TODO @Michael: Finish implementing on()/off() support. For example, jquery.
		removeMethodName: "off",
		/* TODO Need to modify arguments, if JQuery. Compare these two signatures:
				https://api.jquery.com/off/
				https://api.jquery.com/on/		*/
		shouldCleanup: (obj: any) => obj.jquery !== undefined && !Array.from(obj as JQuery).every(o => o.isConnected) /// TODO @Micheal: Test this.
	}
};

interface ValueTypeSameAsKeyType extends Map<object, object> {
	set<T extends object>(key: T, value: T): this;
	get<T extends object>(key: T): T | undefined;
}

/**
 * Example use:
 * private readonly eventListenerManager = new EventListenerManager();
 * ...
 * const elemManaged = this.eventListenerManager.manage(elem);
 * elemManaged.addEventListener("focusin", this.onFocusIn);
 * elemManaged.addEventListener("focusout", this.onFocusOut);
 * ...
 * this.eventListenerManager.removeListeners();
 *
 * TODO @Michael: Describe the use of SupportsEventListenerManager interface
 * TODO @Michael: Describe what is supported by default. For example addEventListener/removeEventListener and on/off
 */
export default class EventListenerManager {
	private readonly managedObjectProxies: ValueTypeSameAsKeyType = new Map();
	private readonly managedObjectListeners = new Map<SupportedTypes | SupportsEventListenerManager, {addMethodName: string, addArgs: any[]}[]>();

	public manage<O extends SupportedTypes | SupportsEventListenerManager>(obj: O): O {
		// Return null or undefined if the object passed is.
		if (obj === null || obj === undefined) {
			return obj;
		}

		// Returned a cached proxy if one exists
		const cachedProxy: O | undefined = this.managedObjectProxies.get(obj);
		if (cachedProxy !== undefined) {
			return cachedProxy;
		}

		// === Build new proxy ===
		const _this = this;
		const proxy = new Proxy(obj, {
			get(target: O, propKey: PropertyKey, receiver) {
				// Is key a string and in the addRemoveDataMap (keys on this are add method names)
				const originalAddMethod = (target as any)[propKey];
				if (typeof propKey === "string" && typeof originalAddMethod === "function") {
					// Note: propKey is the add method's name (e.g. "addEventListener")
					const removeData = EventListenerManager.getRemoveData(target, propKey);
					if (removeData !== undefined) {
						return (...args: any[]) => {
							const result = originalAddMethod.apply(target, args);
							const managedListenerList = _this.managedObjectListeners.get(target);
							const item = {addMethodName: propKey, addArgs: args};
							if (managedListenerList !== undefined) {
								managedListenerList.push(item);
							} else {
								_this.managedObjectListeners.set(target, [item]);
							}
							return result;
						}
					}
				}

				return Reflect.get(target, propKey, receiver);

				// TODO @Michael: Should we be intercepting the remove version and removing it from our list?
			}
		});

		// Cache the proxy
		this.managedObjectProxies.set(obj, proxy);
		return proxy;
	}

	private static getRemoveData<O extends SupportedTypes | SupportsEventListenerManager>(obj: O, addMethodName: string): RemoveData<O> | undefined {
		let removeData: RemoveData<O> | undefined = undefined;
		if (isSupportsEventListenerManager(obj)) {
			const addRemoveData = obj.getEventListenerManagerData();
			removeData = addRemoveData[addMethodName];
		}
		if (removeData === undefined && addMethodName in supportedTypesAddRemoveData) {
			removeData = supportedTypesAddRemoveData[addMethodName];
		}
		return removeData;
	}

	public doesManage(obj: SupportedTypes | SupportsEventListenerManager) {
		return this.managedObjectProxies.has(obj);
	}

	/**
	 * Remove all listeners added through the manager.
	 * If an object (first argument) is provided, only the listeners added to that object are removed.
	 * If no object is provided, all managed listeners are removed.
	 * @param obj
	 */
	public removeListeners(obj?: SupportedTypes | SupportsEventListenerManager): void {
		if (obj !== null && obj !== undefined) {
			const managedListeners = this.managedObjectListeners.get(obj);
			this.managedObjectProxies.delete(obj);
			this.managedObjectListeners.delete(obj);
			if (managedListeners !== undefined) {
				managedListeners.forEach(addData => EventListenerManager.doRemove(obj, addData));
			}
		} else {
			this.managedObjectListeners.forEach((managedListeners, item) => {
				managedListeners.forEach(addData => EventListenerManager.doRemove(item, addData));
			});
			this.managedObjectProxies.clear();
			this.managedObjectListeners.clear();
		}
	}

	private static doRemove(obj: SupportedTypes | SupportsEventListenerManager, addData: {addMethodName: string, addArgs: any[]}) {
		const removeData = EventListenerManager.getRemoveData(obj, addData.addMethodName);
		if (removeData === undefined) {
			// This should never happen.
			throw new Error(`Can not find data to remove listener for "addData.addMethodName" method.`);
		}
		const removeMethodName = removeData.removeMethodName;
		if (typeof (obj as any)[removeMethodName] === "function") {
			(obj as any)[removeMethodName].apply(obj, addData.addArgs)
		} else {
			throw new Error(`Remove method of name "${removeMethodName}" can not be found on object.`);
		}
	}

	/**
	 * Check all objects being managed and for each that are an instance of Node,
	 * remove the listeners from them if they are detached (aka isConnected === false).
	 * This is a method to help you clean up listeners, and if typically needed.
	 */
	public cleanupListeners() {
		// TODO Update to use the data
		this.managedObjectListeners.forEach(({}, managedObject) => {
			if (managedObject instanceof Node && !managedObject.isConnected) {
				this.removeListeners(managedObject);
			}
		});
	}
}